import { useMemo } from 'react'
import { usePersistedState } from '@/hooks/use-persisted-state'
import { StorageKeys, StorageSchemas } from '@/lib/storage'
import {
  SHELL_PANEL_MOTION_DEFAULT,
  normalizeShellPanelMotionStyle,
  shellPanelEnterClass,
  shellPanelExitClass,
  type ShellPanelEnterSide,
  type ShellPanelMotionStyleId,
} from '@/lib/shell-panel-motion'

/**
 * Persisted secondary-dock enter style (gallery #panel-motion picker).
 */
export function useShellPanelMotionStyle(): [
  ShellPanelMotionStyleId,
  (next: ShellPanelMotionStyleId) => void,
] {
  const [raw, setRaw] = usePersistedState<ShellPanelMotionStyleId>({
    key: StorageKeys.UI_SHELL_PANEL_MOTION_STYLE,
    defaultValue: SHELL_PANEL_MOTION_DEFAULT,
    schema: StorageSchemas.shellPanelMotionStyle,
  })

  const style = normalizeShellPanelMotionStyle(raw)
  const setStyle = (next: ShellPanelMotionStyleId) => {
    setRaw(normalizeShellPanelMotionStyle(next))
  }

  return [style, setStyle]
}

/** Class string for SecondaryPanel (and gallery demos) content enter. */
export function useShellPanelEnterClass(side: ShellPanelEnterSide = 'right'): string {
  const [style] = useShellPanelMotionStyle()
  return useMemo(() => shellPanelEnterClass(style, side), [style, side])
}

/** Matching leave. Empty when the chosen style is instant. */
export function useShellPanelExitClass(side: ShellPanelEnterSide = 'right'): string {
  const [style] = useShellPanelMotionStyle()
  return useMemo(() => shellPanelExitClass(style, side), [style, side])
}
